import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import { ENV_CONFIG } from '@ps/core-config';
import { LOADING_CONTEXT } from '@ps/core-overlay';
import { SurveyStatus } from '@ps/survey-kernel';

import {
  type ChildSurveyFilter,
  type FindSurveysParams,
  type GetSurveysParams,
  McTraditionalSurveyPayloadDto,
  McTraditionalSurveyPayloadInput,
  McTraditionalSurveyPayloadMapper,
  QuotaPatchOperationDto,
  QuotaUpdateResultDto,
  StandardSurveyPayloadDto,
  StandardSurveyPayloadInput,
  StandardSurveyPayloadMapper,
  SUPPLIER_QUOTA_PATCH_PATH,
  type SupplierQuotaPatchField,
  type Survey,
  type SurveyHealthMetric,
  type SurveyQuota,
  SurveyQuotaDto,
  type SurveyQuotaUpdate,
  type SurveySearchParams,
  type SurveyStatusCount,
  type SurveySummary,
  type Targeting,
} from '@ps/survey-utils';

import type { GetSurveyByIdDto } from './dtos/get-survey-by-id.dto';
import {
  SurveyCountResponseDto,
  SurveyDto,
  UpdateSurveyStatusPayloadDto,
  SurveyPatchPayloadDto,
  SurveyUrlsPatchResponseDto,
  LaunchTrafficChannelRequestDto,
  TemplateSurveyPayloadDto,
} from './dtos/survey.dto';
import { HighCpiConfirmationDetailsDto } from './dtos/high-cpi-confirmation.dto';
import { SurveyHealthDto } from './dtos/survey-health.dto';
import { RemainingHoursDto } from './dtos/remaining-hours.dto';
import { SurveyHealthMapper } from './mapper/survey-health.mapper';
import { TcChannelDto } from './dtos/projects.dto';
import { SurveyPayloadMapper } from './mapper/survey-payload.mapper';
import {
  BulkGroupSurveysPayloadMapper,
  BulkPatchCpiPayloadMapper,
} from './mapper/bulk-edit-payload.mapper';
import {
  SurveyUrlsPatch,
  SurveyUrlsPatchPayloadMapper,
} from './mapper/survey-urls-patch-payload.mapper';
import { SurveyMapper, TcChannelMapper } from './mapper/survey.mapper';
import { QuotaMapper } from './mapper/quota.mapper';
import { QuotaUpdateMapper } from './mapper/quota-update.mapper';
import { TemplateSurveyPayloadMapper } from './mapper/template-survey.payload.mapper';
import { BillingRuleRecord } from '@ps/user-settings-models';
import { LoadingScope, withLoading } from '@ps/core-loading';
import {
  SurveyLookupParamsDto,
  SurveyLookupResponseDto,
} from './dtos/survey-lookup.dto';
import { SurveyLookupPayloadMapper } from './mapper/survey-lookup.payload.mapper';

/** A single page of survey quotas plus the total count reported by the API. */
export interface QuotaPage {
  quotas: SurveyQuota[];
  total: number;
}

interface GetSurveyDetailsParams {
  id: number;
  targeting?: Targeting;
  accessible?: boolean;
  scope?: LoadingScope;
}

const CHILD_SURVEYS_PARAMS: Record<string, string | number> = {
  page: 1,
  status: 'all',
  targeting: 0,
};

@Injectable({ providedIn: 'root' })
export class SurveyApi {
  private readonly _env = inject(ENV_CONFIG);
  private readonly _http = inject(HttpClient);
  private readonly _baseUrl = `${this._env.pureSpectrum.url}`;
  private readonly _statisticsUrl =
    this._env.pureSpectrum.surveyStatiscticsServiceUrl;
  private readonly _mapper = new SurveyMapper();
  private readonly _tcChannelMapper = new TcChannelMapper();
  private readonly _payloadMapper = new SurveyPayloadMapper();
  private readonly _quotaMapper = new QuotaMapper();
  private readonly _quotaUpdateMapper = new QuotaUpdateMapper();
  private readonly _bulkGroupSurveysPayloadMapper =
    new BulkGroupSurveysPayloadMapper();
  private readonly _cpiMapper = new BulkPatchCpiPayloadMapper();
  private readonly _surveyUrlsPatchMapper = new SurveyUrlsPatchPayloadMapper();
  private readonly _lookupPayloadMapper = new SurveyLookupPayloadMapper();
  private readonly _healthMapper = new SurveyHealthMapper();

  public getAll(data: GetSurveysParams): Observable<Survey[]> {
    const params = new HttpParams({
      fromObject: { ...this._payloadMapper.toPayload(data) },
    });
    return this._http
      .get<SurveyDto[]>(`${this._baseUrl}/buyers/v2/surveys?UI=1`, { params })
      .pipe(map((surveys) => this._mapper.fromDtoList(surveys)));
  }

  public getCounts(
    surveysParams: GetSurveysParams,
  ): Observable<SurveyStatusCount> {
    const params = new HttpParams({
      fromObject: { ...this._payloadMapper.toCountsPayload(surveysParams) },
    });
    return this._http
      .get<SurveyCountResponseDto>(
        `${this._baseUrl}/buyers/v2/surveys/count?UI=1`,
        { params },
      )
      .pipe(map((response) => this._mapper.fromCountResponseDto(response)));
  }

  public getChildSurveys(
    filter: ChildSurveyFilter,
    targeting: Targeting = 0,
  ): Observable<Survey[]> {
    const params = new HttpParams({
      fromObject: { ...CHILD_SURVEYS_PARAMS, targeting, ...filter },
    });
    return this._http
      .get<SurveyDto[]>(`${this._baseUrl}/buyers/v2/surveys?UI=1`, { params })
      .pipe(map((surveys) => this._mapper.fromDtoList(surveys)));
  }

  public getTrafficChannelChildren(surveyId: number): Observable<Survey[]> {
    return this._http
      .get<
        TcChannelDto[]
      >(`${this._baseUrl}/buyers/v2/surveys/${surveyId}/channels?UI=1`, { context: withLoading('drawer') })
      .pipe(map((dtos) => this._tcChannelMapper.fromDtoList(dtos)));
  }

  public updateSurveyStatus(
    surveyId: number,
    status: SurveyStatus,
    scope: LoadingScope,
  ): Observable<void> {
    const body: UpdateSurveyStatusPayloadDto = { ps_survey_status: status };
    return this._http.put<void>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/status?UI=1`,
      body,
      {
        context: withLoading(scope),
      },
    );
  }

  public getById({
    id,
    targeting = 0,
    accessible = false,
    scope = 'drawer',
  }: GetSurveyDetailsParams): Observable<Survey> {
    const params = new HttpParams({
      fromObject: {
        UI: 1,
        targeting,
        ...(accessible && { accessible }),
      },
    });
    return this._http
      .get<GetSurveyByIdDto>(`${this._baseUrl}/buyers/v2/surveys/${id}`, {
        params,
        context: withLoading(scope),
      })
      .pipe(map((dto) => this._mapper.fromDetailsDto(dto)));
  }

  public getHighCpiDetails(
    surveyId: number,
  ): Observable<HighCpiConfirmationDetailsDto> {
    return this._http.get<HighCpiConfirmationDetailsDto>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/high-cpi-details`,
      {
        context: withLoading('drawer'),
      },
    );
  }

  public getHealth(surveyId: number): Observable<SurveyHealthMetric[]> {
    return this._http
      .get<
        SurveyHealthDto[]
      >(`${this._baseUrl}/buyers/v2/surveys/${surveyId}/health`, { context: withLoading('drawer') })
      .pipe(map((dtos) => this._healthMapper.fromDtoList(dtos)));
  }

  public getRemainingHours(surveyId: number): Observable<number> {
    return this._http
      .get<RemainingHoursDto>(
        `${this._statisticsUrl}/surveys/remaining-hours/${surveyId}`,
        { context: withLoading('drawer') },
      )
      .pipe(map((dto) => dto.remainingHour));
  }

  // Most patches ignore the response, hence the `void` default; a caller that
  // reads it names the shape it expects instead of casting one on afterwards.
  public patchSurvey<TResponse = void>(
    surveyId: number,
    payload: SurveyPatchPayloadDto[],
    scope: LoadingScope,
  ): Observable<TResponse> {
    return this._http.patch<TResponse>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}`,
      payload,
      { context: withLoading(scope) },
    );
  }

  // Unlike other patches this one reads the response: it answers with a
  // freshly regenerated entry link.
  public updateLiveTestUrls(
    surveyId: number,
    urls: SurveyUrlsPatch,
  ): Observable<string> {
    return this.patchSurvey<SurveyUrlsPatchResponseDto>(
      surveyId,
      this._surveyUrlsPatchMapper.toPayload(urls),
      'page',
    ).pipe(map((dto) => dto.test_ps_survey_entry_link ?? ''));
  }

  public updateCustomRedirectField(
    surveyId: number,
    value: string,
  ): Observable<void> {
    return this._http.put<void>(
      `${this._baseUrl}/suppliers/v2/surveys/${surveyId}/custom-redirect-field`,
      { supplier_custom_redirect_field: value },
      { context: withLoading('drawer') },
    );
  }

  public updateCurrentCpi(
    surveyId: number,
    cpi: number,
    scope: LoadingScope = 'drawer',
  ): Observable<void> {
    return this.patchSurvey(
      surveyId,
      this._cpiMapper.toPayload({ updatedValue: cpi, error: null }),
      scope,
    );
  }

  public updateOfferPrice(
    surveyId: number,
    cpi: number,
    scope: LoadingScope = 'drawer',
  ): Observable<void> {
    return this._http.put<void>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/offerPrice?UI=1`,
      { value: cpi },
      { context: withLoading(scope) },
    );
  }

  public getQuotas(
    surveyId: number,
    page?: number,
    limit?: number,
    search?: string,
  ): Observable<QuotaPage> {
    const params = new HttpParams({
      fromObject: {
        UI: 1,
        QBS: 1,
        ...(page ? { page } : {}),
        ...(limit ? { limit } : {}),
        ...(search ? { search } : {}),
      },
    });
    return this._http
      .get<
        SurveyQuotaDto[]
      >(`${this._baseUrl}/buyers/v2/surveys/${surveyId}/quotas`, { params, observe: 'response', context: withLoading('drawer') })
      .pipe(
        map((response) => ({
          quotas: this._quotaMapper.fromDtoList(response.body ?? []),
          total: Number(response.headers.get('total')) || 0,
        })),
      );
  }

  public setQuotaLock(
    surveyId: number,
    psQuotaId: string,
    locked: boolean,
  ): Observable<void> {
    const payload: QuotaPatchOperationDto[] = [
      { op: 'replace', path: '/lock', value: locked },
    ];
    return this._http.patch<void>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/quotas/${psQuotaId}`,
      payload,
      {
        context: withLoading('drawer'),
      },
    );
  }

  public setQuotaCurrentTarget(
    surveyId: number,
    psQuotaId: string,
    currentTarget: number,
    updateSupplier: boolean,
  ): Observable<SurveyQuotaUpdate> {
    const payload: QuotaPatchOperationDto[] = [
      {
        op: 'replace',
        path: '/current_target',
        value: String(currentTarget),
        updateSupplier,
      },
    ];
    return this._http
      .patch<QuotaUpdateResultDto>(
        `${this._baseUrl}/buyers/v2/surveys/${surveyId}/quotas/${psQuotaId}`,
        payload,
        {
          context: withLoading('drawer'),
        },
      )
      .pipe(map((dto) => this._quotaUpdateMapper.fromDto(dto)));
  }

  public setQuotaCurrentlyOpen(
    surveyId: number,
    psQuotaId: string,
    currentlyOpen: number,
  ): Observable<SurveyQuotaUpdate> {
    return this._patchQuota<QuotaUpdateResultDto>(surveyId, psQuotaId, {
      op: 'replace',
      path: '/current_open',
      value: String(currentlyOpen),
    }).pipe(map((dto) => this._quotaUpdateMapper.fromDto(dto)));
  }

  public setSupplierQuotaField(
    surveyId: number,
    psQuotaId: string,
    supplierRefId: string,
    field: SupplierQuotaPatchField,
    value: number,
  ): Observable<void> {
    const payload: QuotaPatchOperationDto[] = [
      {
        op: 'replace',
        path: SUPPLIER_QUOTA_PATCH_PATH[field],
        value: String(value),
      },
    ];
    return this._http.patch<void>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/quotas/${psQuotaId}/suppliers/${supplierRefId}`,
      payload,
      {
        context: withLoading('drawer'),
      },
    );
  }

  public renameQuota(
    surveyId: number,
    psQuotaId: string,
    label: string,
  ): Observable<void> {
    return this._patchQuota(surveyId, psQuotaId, {
      op: 'replace',
      path: '/quota_title',
      value: label,
    });
  }

  public setQuotaCpi(
    surveyId: number,
    psQuotaId: string,
    cpi: number,
  ): Observable<void> {
    return this._patchQuota(surveyId, psQuotaId, {
      op: 'replace',
      path: '/cpi',
      value: cpi,
    });
  }

  public pushApplyPriceRecoButtonCount(surveyId: number): Observable<void> {
    return this._http.put<void>(
      `${this._baseUrl}/buyers/v2/pricing/notifications/apply-button/${surveyId}`,
      null,
      {
        context: withLoading('drawer'),
      },
    );
  }

  public launchTrafficChannel(
    surveyId: number,
    payload: LaunchTrafficChannelRequestDto,
    bulk = false,
  ): Observable<Survey[]> {
    const params = new HttpParams({
      fromObject: { UI: '1', ...(bulk && { bulk: 'true' }) },
    });
    return this._http
      .post<
        TcChannelDto[]
      >(`${this._baseUrl}/buyers/v2/surveys/${surveyId}/channels`, payload, { params, context: withLoading('drawer') })
      .pipe(map((dtos) => this._tcChannelMapper.fromDtoList(dtos)));
  }

  public searchInvoicedSurveysWithBillingId(
    billingId: string,
  ): Observable<void> {
    const params = new HttpParams({
      fromObject: { billing: billingId },
    });
    return this._http.get<void>(
      `${this._baseUrl}/buyers/v2/surveys/invoiced/billing_id`,
      { params },
    );
  }

  public verifyBillingIdAgainstBillingRules(
    billingId: string,
    billingRuleRecord: BillingRuleRecord,
  ): Observable<void> {
    return this._http.post<void>(
      `${this._baseUrl}/buyers/v2/surveys/billig_id/rules/`,
      { po: billingId, billingRules: billingRuleRecord },
    );
  }

  public groupSurveys(
    surveyIds: number[],
    groupTitle: string,
  ): Observable<void> {
    const body = this._bulkGroupSurveysPayloadMapper.toPayload(
      surveyIds,
      groupTitle,
    );
    return this._http.post<void>(
      `${this._baseUrl}/buyers/v2/surveys/group`,
      body,
      { context: withLoading('drawer') },
    );
  }

  public deleteSurvey(surveyId: number): Observable<void> {
    return this._http.delete<void>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}`,
      { context: withLoading('drawer') },
    );
  }

  /** The applied Advanced Filters lookup — criteria and date range, no keyword. */
  public getSurveySummaries(
    params: FindSurveysParams,
  ): Observable<SurveySummary[]> {
    return this._lookup(this._lookupPayloadMapper.toPayload(params));
  }

  /** A typeahead search — one keyword against one field. Same endpoint, different purpose. */
  public searchSurveySummaries(
    params: SurveySearchParams,
  ): Observable<SurveySummary[]> {
    return this._lookup(
      this._lookupPayloadMapper.toSearchSurveyPayload(params),
    );
  }

  public createSurveyFromTemplate(
    templateId: number,
    userId: number,
    companyId: number,
  ): Observable<SurveyDto> {
    const payload = new TemplateSurveyPayloadMapper().toPayload({
      templateId,
      userId,
      companyId,
    });
    return this._createSurvey(payload);
  }

  public createStandardSurvey(
    input: StandardSurveyPayloadInput,
  ): Observable<SurveyDto> {
    return this._createSurvey(
      new StandardSurveyPayloadMapper().toPayload(input),
    );
  }

  public createMcTraditionalSurvey(
    input: McTraditionalSurveyPayloadInput,
  ): Observable<Survey> {
    return this._createSurvey(
      new McTraditionalSurveyPayloadMapper().toPayload(input),
    ).pipe(map((dto) => this._mapper.fromDetailsDto(dto)));
  }

  // No `targeting` query param: it only shapes the response (defaulting to 1
  // returns the rehydrated quotas + qualifications), it never gates the write.
  public updateStandardSurvey(
    surveyId: number,
    input: StandardSurveyPayloadInput,
  ): Observable<SurveyDto> {
    const payload = new StandardSurveyPayloadMapper().toPayload(input);
    return this._http.put<SurveyDto>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}?UI=1`,
      payload,
      {
        context: new HttpContext().set(LOADING_CONTEXT, 'page'),
      },
    );
  }

  public cloneStandardSurvey(
    surveyId: number,
    isServiceOperatorCreation: boolean,
  ): Observable<SurveyDto> {
    const params = new HttpParams({
      fromObject: {
        UI: 1,
        targeting: 1,
        ...(isServiceOperatorCreation && { serviceOperatorCreation: 1 }),
      },
    });
    return this._http.post<SurveyDto>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/clone`,
      null,
      {
        params,
        context: new HttpContext().set(LOADING_CONTEXT, 'page'),
      },
    );
  }

  private _createSurvey(
    payload:
      | StandardSurveyPayloadDto
      | McTraditionalSurveyPayloadDto
      | TemplateSurveyPayloadDto,
  ): Observable<SurveyDto> {
    return this._http.post<SurveyDto>(
      `${this._baseUrl}/buyers/v2/surveys?UI=1`,
      payload,
      { context: withLoading('page') },
    );
  }

  private _lookup(payload: SurveyLookupParamsDto): Observable<SurveySummary[]> {
    const httpParams = new HttpParams({ fromObject: { ...payload } });

    return this._http
      .get<
        SurveyLookupResponseDto[]
      >(`${this._baseUrl}/buyers/v2/surveys/lookup`, { params: httpParams, context: withLoading('drawer') })
      .pipe(map((dtos) => dtos.map((dto) => this._mapper.fromLookupDto(dto))));
  }

  private _patchQuota<T = void>(
    surveyId: number,
    psQuotaId: string,
    operation: QuotaPatchOperationDto,
  ): Observable<T> {
    return this._http.patch<T>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/quotas/${psQuotaId}`,
      [operation],
      {
        context: withLoading('drawer'),
      },
    );
  }
}
